import React, { useMemo, useState } from 'react';
import { useData, ESTADOS_CITA } from '../context/DataContext';
import { SERVICE_STYLES } from '../components/ServiceTag';
import { SHEET_XLSX_URL } from '../api';

function iso(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function haceDias(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
}
function inicioDeMes() {
  const d = new Date();
  return iso(new Date(d.getFullYear(), d.getMonth(), 1));
}

const ATAJOS = [
  { label: 'Hoy', calc: () => [iso(), iso()] },
  { label: 'Últimos 7 días', calc: () => [haceDias(6), iso()] },
  { label: 'Últimos 30 días', calc: () => [haceDias(29), iso()] },
  { label: 'Este mes', calc: () => [inicioDeMes(), iso()] },
];

export default function Reportes() {
  const { clientes, citas } = useData();

  // Vacío = sin filtro, se muestra todo el histórico.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  // Las fechas del Sheet ya vienen normalizadas a YYYY-MM-DD, así que comparar
  // como cadena es correcto y evita problemas de zona horaria.
  const enRango = useMemo(() => (fecha) => {
    if (!desde && !hasta) return true;
    if (!fecha) return false;
    if (desde && fecha < desde) return false;
    if (hasta && fecha > hasta) return false;
    return true;
  }, [desde, hasta]);

  const clientesFiltrados = useMemo(() => clientes.filter(c => enRango(c.fecha)), [clientes, enRango]);
  const citasFiltradas = useMemo(() => citas.filter(c => enRango(c.fecha)), [citas, enRango]);

  const stats = useMemo(() => {
    const porServicio = {};
    clientesFiltrados.forEach(c => {
      const s = c.servicio || 'Sin especificar';
      porServicio[s] = (porServicio[s] || 0) + 1;
    });
    const porEstado = {};
    ESTADOS_CITA.forEach(e => { porEstado[e] = 0; });
    citasFiltradas.forEach(c => { porEstado[c.estado] = (porEstado[c.estado] || 0) + 1; });
    const porCiudad = {};
    clientesFiltrados.forEach(c => {
      const k = c.ciudad || 'Sin especificar';
      porCiudad[k] = (porCiudad[k] || 0) + 1;
    });
    return {
      totalClientes: clientesFiltrados.length,
      agendados: clientesFiltrados.filter(c => c.estatus === 'Agregado').length,
      totalCitas: citasFiltradas.length,
      completadas: citasFiltradas.filter(c => c.estado === 'Completada').length,
      canceladas: citasFiltradas.filter(c => c.estado.startsWith('Cancelada')).length,
      sinFecha: clientes.filter(c => !c.fecha).length,
      porServicio, porEstado, porCiudad,
    };
  }, [clientesFiltrados, citasFiltradas, clientes]);

  const maxServ = Math.max(1, ...Object.values(stats.porServicio));
  const hayFiltro = Boolean(desde || hasta);

  const aplicarAtajo = (calc) => { const [d, h] = calc(); setDesde(d); setHasta(h); };

  const Card = ({ label, value, color = '#185fa5' }) => (
    <div className="card" style={{ padding: '16px 18px', flex: '1 1 150px' }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#475569', fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );

  const rotulo = () => {
    if (!hayFiltro) return 'Todo el histórico';
    if (desde && hasta) return `Del ${desde} al ${hasta}`;
    if (desde) return `Desde el ${desde}`;
    return `Hasta el ${hasta}`;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ marginRight: 'auto' }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Reportes</h1>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{rotulo()}</div>
        </div>
        <a href={SHEET_XLSX_URL} target="_blank" rel="noreferrer">
          <button className="btn-primary">⬇️ Descargar Sheet (.xlsx)</button>
        </a>
      </div>

      {/* ── Filtro por rango de fechas ── */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Desde</label>
            <input type="date" value={desde} max={hasta || undefined} onChange={e => setDesde(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Hasta</label>
            <input type="date" value={hasta} min={desde || undefined} onChange={e => setHasta(e.target.value)} />
          </div>
          {hayFiltro && (
            <button onClick={() => { setDesde(''); setHasta(''); }}
              style={{ padding: '9px 14px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13 }}>
              Limpiar
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {ATAJOS.map(a => (
            <button key={a.label} onClick={() => aplicarAtajo(a.calc)}
              style={{ padding: '6px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 999, fontWeight: 600, fontSize: 12 }}>
              {a.label}
            </button>
          ))}
        </div>

        {hayFiltro && stats.sinFecha > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#b45309', background: '#fef3c7', padding: '7px 10px', borderRadius: 8 }}>
            {stats.sinFecha} registro{stats.sinFecha !== 1 ? 's' : ''} sin fecha en el Sheet quedan fuera del rango.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Card label="Clientes totales" value={stats.totalClientes} />
        <Card label="Agendados" value={stats.agendados} color="#16a34a" />
        <Card label="Citas totales" value={stats.totalCitas} />
        <Card label="Completadas" value={stats.completadas} color="#16a34a" />
        <Card label="Canceladas" value={stats.canceladas} color="#dc2626" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, marginBottom: 14 }}>Clientes por tipo de servicio</div>
          {Object.entries(stats.porServicio).sort((a, b) => b[1] - a[1]).map(([serv, n]) => {
            const style = SERVICE_STYLES[serv] || { background: '#f1f5f9', color: '#475569' };
            return (
              <div key={serv} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, marginBottom: 3 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{serv}</span>
                  <span>{n}</span>
                </div>
                <div style={{ background: '#f1f5f9', borderRadius: 999, height: 10 }}>
                  <div style={{
                    width: `${(n / maxServ) * 100}%`, height: '100%',
                    background: style.color, borderRadius: 999, minWidth: 6
                  }} />
                </div>
              </div>
            );
          })}
          {!Object.keys(stats.porServicio).length && <div style={{ fontSize: 13, color: '#94a3b8' }}>Sin datos en este rango.</div>}
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ fontWeight: 700, padding: '16px 18px 8px' }}>Citas por estado</div>
          <table>
            <thead><tr><th>Estado</th><th style={{ textAlign: 'right' }}>Citas</th></tr></thead>
            <tbody>
              {ESTADOS_CITA.map(e => (
                <tr key={e}>
                  <td>{e}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{stats.porEstado[e] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ fontWeight: 700, padding: '16px 18px 8px' }}>Clientes por ciudad</div>
          <table>
            <thead><tr><th>Ciudad</th><th style={{ textAlign: 'right' }}>Clientes</th></tr></thead>
            <tbody>
              {Object.entries(stats.porCiudad).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([ciudad, n]) => (
                <tr key={ciudad}>
                  <td>{ciudad}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{n}</td>
                </tr>
              ))}
              {!Object.keys(stats.porCiudad).length && (
                <tr><td colSpan={2} style={{ color: '#94a3b8', fontSize: 13 }}>Sin datos en este rango.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
